/**
 * Fog-of-war reveal grid math (cells around walk positions).
 * Persistence lives on the signed-in user profile in Firestore — see userProfile.js.
 */

/** Small cells so a ~7m walk disk still punches fog (centers must fall in radius). */
export const CELL_SIZE_M = 5;
export const REVEAL_RADIUS_M = 7;

/** Legacy localStorage key — consumed once on sign-in migration, then removed. */
export const FOG_REVEAL_KEY = 'm2b-fog-reveal-v1';

const METERS_PER_DEG_LAT = 111320;
const CELL_KEY_RE = /^-?\d+:-?\d+$/;

function metersPerDegLng(lat) {
  return METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
}

function browserStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Approximate distance in meters between two WGS84 points. */
export function distanceMeters(lng1, lat1, lng2, lat2) {
  const x = (lng2 - lng1) * metersPerDegLng((lat1 + lat2) / 2);
  const y = (lat2 - lat1) * METERS_PER_DEG_LAT;
  return Math.sqrt(x * x + y * y);
}

export function latLngToCell(lng, lat, cellSizeM = CELL_SIZE_M) {
  const row = Math.floor((lat * METERS_PER_DEG_LAT) / cellSizeM);
  const col = Math.floor((lng * metersPerDegLng(lat)) / cellSizeM);
  return `${row}:${col}`;
}

export function cellCenter(cellKey, cellSizeM = CELL_SIZE_M) {
  const [rowStr, colStr] = String(cellKey).split(':');
  const row = Number(rowStr);
  const col = Number(colStr);
  if (!Number.isFinite(row) || !Number.isFinite(col)) {
    return null;
  }
  const lat = ((row + 0.5) * cellSizeM) / METERS_PER_DEG_LAT;
  const lng = ((col + 0.5) * cellSizeM) / metersPerDegLng(lat);
  return { lng, lat };
}

/**
 * Return cell keys within radiusM of (lng, lat).
 */
export function cellsInRadius(lng, lat, radiusM = REVEAL_RADIUS_M, cellSizeM = CELL_SIZE_M) {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return [];

  const latDelta = radiusM / METERS_PER_DEG_LAT;
  const lngDelta = radiusM / metersPerDegLng(lat);
  const minLat = lat - latDelta;
  const maxLat = lat + latDelta;
  const minLng = lng - lngDelta;
  const maxLng = lng + lngDelta;

  const minRow = Math.floor((minLat * METERS_PER_DEG_LAT) / cellSizeM);
  const maxRow = Math.floor((maxLat * METERS_PER_DEG_LAT) / cellSizeM);
  const minCol = Math.floor((minLng * metersPerDegLng(lat)) / cellSizeM);
  const maxCol = Math.floor((maxLng * metersPerDegLng(lat)) / cellSizeM);

  const cells = [];
  for (let row = minRow; row <= maxRow; row += 1) {
    for (let col = minCol; col <= maxCol; col += 1) {
      const key = `${row}:${col}`;
      const center = cellCenter(key, cellSizeM);
      if (center && distanceMeters(lng, lat, center.lng, center.lat) <= radiusM) {
        cells.push(key);
      }
    }
  }
  return cells;
}

export function isFogCellKey(value) {
  return typeof value === 'string' && CELL_KEY_RE.test(value);
}

export function normalizeCellSet(value) {
  if (!Array.isArray(value)) return [];
  const unique = new Set();
  value.forEach((cell) => {
    if (isFogCellKey(cell)) unique.add(cell);
  });
  return Array.from(unique);
}

/** Read fogReveal map from a user profile into a cell-key array. */
export function fogRevealCellsFromProfile(profile) {
  const map = profile?.fogReveal;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
  return Object.keys(map).filter((key) => map[key] && isFogCellKey(key));
}

/**
 * One-time migration helper: read legacy localStorage cells and clear the key.
 * Returns [] when nothing is stored.
 */
export function consumeLegacyFogReveal(storage = browserStorage()) {
  if (!storage) return [];

  try {
    const raw = storage.getItem(FOG_REVEAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const cells = normalizeCellSet(parsed);
    storage.removeItem(FOG_REVEAL_KEY);
    return cells;
  } catch {
    try {
      storage.removeItem(FOG_REVEAL_KEY);
    } catch {
      /* ignore */
    }
    return [];
  }
}

/**
 * Merge cells revealed by a walk/simulate position into an existing set.
 * Returns a new array (does not mutate).
 */
export function revealAt(cells, lng, lat, radiusM = REVEAL_RADIUS_M, cellSizeM = CELL_SIZE_M) {
  const next = new Set(normalizeCellSet(cells));
  cellsInRadius(lng, lat, radiusM, cellSizeM).forEach((key) => next.add(key));
  return Array.from(next);
}

/** Cells in `next` that are not already in `prev`. */
export function newFogCells(prev, next) {
  const before = new Set(normalizeCellSet(prev));
  return normalizeCellSet(next).filter((key) => !before.has(key));
}

/**
 * Offset a WGS84 point by north/east meters (for simulate-walk nudges).
 */
export function offsetLatLng(lng, lat, northM, eastM) {
  const nextLat = lat + northM / METERS_PER_DEG_LAT;
  const nextLng = lng + eastM / metersPerDegLng(lat);
  return { lng: nextLng, lat: nextLat };
}

/**
 * Walkable Sarajevo play area for “% explored” (urban core around Baščaršija /
 * Marijin Dvor — not the full municipal cadastral area).
 * Roughly ~9 km × ~6 km ≈ 18k cells at 40 m.
 */
export const SARAJEVO_EXPLORE_BOUNDS = Object.freeze({
  west: 18.35,
  east: 18.46,
  south: 43.825,
  north: 43.88,
});

export function pointInBounds(lng, lat, bounds = SARAJEVO_EXPLORE_BOUNDS) {
  return (
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= bounds.west &&
    lng <= bounds.east &&
    lat >= bounds.south &&
    lat <= bounds.north
  );
}

/** Uniform-random point inside the play bbox. */
export function randomPointInBounds(bounds = SARAJEVO_EXPLORE_BOUNDS) {
  return {
    lng: bounds.west + Math.random() * (bounds.east - bounds.west),
    lat: bounds.south + Math.random() * (bounds.north - bounds.south),
  };
}

/**
 * Random point in bounds, spaced away from existing drops when possible.
 * Falls back to the last candidate after maxAttempts rerolls.
 */
export function pickRandomDropSpot(
  existingPoints = [],
  {
    bounds = SARAJEVO_EXPLORE_BOUNDS,
    minSpacingM = REVEAL_RADIUS_M * 2,
    maxAttempts = 20,
  } = {}
) {
  let candidate = randomPointInBounds(bounds);
  for (let i = 0; i < maxAttempts; i += 1) {
    const tooClose = (existingPoints || []).some(
      (p) =>
        Number.isFinite(p?.lng) &&
        Number.isFinite(p?.lat) &&
        distanceMeters(candidate.lng, candidate.lat, p.lng, p.lat) < minSpacingM
    );
    if (!tooClose) break;
    candidate = randomPointInBounds(bounds);
  }
  return candidate;
}

/** Approximate cell count covering the play bbox (area / cell area). */
export function estimateCellsInBounds(bounds = SARAJEVO_EXPLORE_BOUNDS, cellSizeM = CELL_SIZE_M) {
  const midLat = (bounds.south + bounds.north) / 2;
  const heightM = (bounds.north - bounds.south) * METERS_PER_DEG_LAT;
  const widthM = (bounds.east - bounds.west) * metersPerDegLng(midLat);
  return Math.max(1, Math.round((heightM * widthM) / (cellSizeM * cellSizeM)));
}

export function countRevealedCellsInBounds(
  cells,
  bounds = SARAJEVO_EXPLORE_BOUNDS,
  cellSizeM = CELL_SIZE_M
) {
  return normalizeCellSet(cells).reduce((n, key) => {
    const center = cellCenter(key, cellSizeM);
    if (center && pointInBounds(center.lng, center.lat, bounds)) return n + 1;
    return n;
  }, 0);
}

/**
 * Percent of the Sarajevo play bbox revealed (0–100).
 * Denominator ≈ urban-core cell budget, not all of Bosnia.
 */
export function exploredSarajevoPercent(
  cells,
  bounds = SARAJEVO_EXPLORE_BOUNDS,
  cellSizeM = CELL_SIZE_M
) {
  const total = estimateCellsInBounds(bounds, cellSizeM);
  const revealed = countRevealedCellsInBounds(cells, bounds, cellSizeM);
  if (total <= 0) return 0;
  return Math.min(100, (revealed / total) * 100);
}
